/**
 * Send Update modal.
 *
 * Open it with `task` + `open` + `onOpenChange`. On open, drafts via
 * aiSendUpdateApi.draft (skeleton state while loading). User edits the
 * subject, body, and questions inline; clicking Send uses connected Gmail
 * when available, with Make as fallback. Cancel/Esc/backdrop all call
 * /send-update/cancel so we don't leak `pending_send` log rows.
 */

import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2, Plus, Send, Sparkles, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  aiSendUpdateApi,
  ApiClientError,
  communicationsApi,
  type AiSendUpdateDraftResponse,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import type { Task } from '@oneup/shared';

interface SendUpdateModalProps {
  task: Task;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent?: () => void;
}

interface DraftState {
  state: 'loading' | 'ready' | 'error';
  logId: string | null;
  subject: string;
  body: string;
  questions: string[];
  fromEmail: string;
  fromOptions: string[];
  senderSource: 'gmail' | 'fallback';
  connectedEmail?: string;
  toEmailsText: string;
  recipientOptions: AiSendUpdateDraftResponse['sendDefaults']['recipientOptions'];
  errorMessage?: string;
}

const INITIAL_DRAFT: DraftState = {
  state: 'loading',
  logId: null,
  subject: '',
  body: '',
  questions: [],
  fromEmail: 'dakota@oneupdigitalmarketing.com',
  fromOptions: ['alice@oneupdigitalmarketing.com', 'dakota@oneupdigitalmarketing.com'],
  senderSource: 'fallback',
  toEmailsText: '',
  recipientOptions: [],
};

export function SendUpdateModal({ task, open, onOpenChange, onSent }: SendUpdateModalProps) {
  const [draft, setDraft] = useState<DraftState>(INITIAL_DRAFT);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendMode, setSendMode] = useState('auto');

  const fetchDraft = useCallback(async () => {
    setDraft(INITIAL_DRAFT);
    setSendError(null);
    try {
      const result = await aiSendUpdateApi.draft(task.id);
      setDraft({
        state: 'ready',
        logId: result.logId,
        subject: result.subject,
        body: result.body,
        questions: result.questions,
        fromEmail: result.sendDefaults.fromEmail,
        fromOptions: result.sendDefaults.fromOptions,
        senderSource: result.sendDefaults.senderSource,
        connectedEmail: result.sendDefaults.connectedEmail,
        toEmailsText: result.sendDefaults.toEmails.join(', '),
        recipientOptions: result.sendDefaults.recipientOptions,
      });
    } catch (err) {
      setDraft({
        state: 'error',
        logId: null,
        subject: '',
        body: '',
        questions: [],
        fromEmail: 'dakota@oneupdigitalmarketing.com',
        fromOptions: ['alice@oneupdigitalmarketing.com', 'dakota@oneupdigitalmarketing.com'],
        senderSource: 'fallback',
        toEmailsText: '',
        recipientOptions: [],
        errorMessage: formatError(err),
      });
    }
  }, [task.id]);

  // Fire the draft request whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setSendMode('auto');
    void fetchDraft();
  }, [open, fetchDraft]);

  const linkedEmailsQuery = useQuery({
    queryKey: ['communications', 'task-linked-emails', task.id],
    queryFn: () =>
      communicationsApi.list({
        taskId: task.id,
        source: 'gmail',
        limit: 12,
      }),
    enabled: open,
    staleTime: 30_000,
  });
  const gmailAliasesQuery = useQuery({
    queryKey: ['communications', 'gmail-send-as'],
    queryFn: () => communicationsApi.sendAs(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const linkedEmails = linkedEmailsQuery.data?.items ?? [];
  const gmailFromOptions = (gmailAliasesQuery.data?.aliases ?? []).map((alias) => alias.email);
  const isGmailReplyMode = sendMode !== 'auto';
  const effectiveFromOptions = isGmailReplyMode ? gmailFromOptions : draft.fromOptions;
  const selectedLinkedEmail = linkedEmails.find((item) => item.recordId === sendMode);

  useEffect(() => {
    if (!isGmailReplyMode || gmailFromOptions.length === 0) return;
    if (gmailFromOptions.includes(draft.fromEmail)) return;
    setDraft((prev) => ({ ...prev, fromEmail: gmailFromOptions[0] ?? prev.fromEmail }));
  }, [draft.fromEmail, gmailFromOptions, isGmailReplyMode]);

  const sendMutation = useMutation({
    mutationFn: () => {
      const questions = draft.questions.filter((q) => q.trim().length > 0);
      if (isGmailReplyMode) {
        const threadId =
          typeof selectedLinkedEmail?.metadata.gmailThreadId === 'string'
            ? selectedLinkedEmail.metadata.gmailThreadId
            : undefined;
        const bodyMarkdown = questions.length
          ? `${draft.body.trim()}\n\nQuestions:\n${questions.map((q) => `- ${q}`).join('\n')}`
          : draft.body;
        return communicationsApi.sendGmail({
          fromEmail: draft.fromEmail,
          toEmails: splitEmails(draft.toEmailsText),
          subject: draft.subject,
          bodyMarkdown,
          replyToRecordId: selectedLinkedEmail?.recordId,
          threadId,
          taskId: task.id,
          clientId: task.client?.id,
        }).then((result) => ({
          ok: true as const,
          payload: { gmail: result },
        }));
      }
      return aiSendUpdateApi.send(task.id, {
        logId: draft.logId,
        subject: draft.subject,
        body: draft.body,
        questions,
        fromEmail: draft.fromEmail,
        toEmails: splitEmails(draft.toEmailsText),
        deliveryMode: 'auto',
      });
    },
    onSuccess: () => {
      if (isGmailReplyMode && draft.logId) {
        void aiSendUpdateApi.cancel(task.id, draft.logId).catch(() => undefined);
      }
      onSent?.();
      onOpenChange(false);
    },
    onError: (err) => setSendError(formatError(err)),
  });

  const cancelDraft = useCallback(() => {
    if (draft.state === 'ready' && draft.logId) {
      // Fire-and-forget; we don't block the close on this.
      void aiSendUpdateApi.cancel(task.id, draft.logId).catch(() => undefined);
    }
  }, [draft, task.id]);

  // When the user closes via Esc, backdrop, or the X — fire cancel.
  // sendMutation.success path also closes the modal but bypasses this since
  // we already updated the row to status=sent.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && draft.state === 'ready' && !sendMutation.isSuccess) {
        cancelDraft();
      }
      onOpenChange(next);
    },
    [draft.state, sendMutation.isSuccess, cancelDraft, onOpenChange],
  );

  const updateQuestion = (idx: number, value: string) =>
    setDraft((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === idx ? value : q)),
    }));
  const addQuestion = () => setDraft((prev) => ({ ...prev, questions: [...prev.questions, ''] }));
  const removeQuestion = (idx: number) =>
    setDraft((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  const addRecipient = (email: string) =>
    setDraft((prev) => ({
      ...prev,
      toEmailsText: splitEmails(`${prev.toEmailsText}, ${email}`).join(', '),
    }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
            <DialogTitle>Send Update — AI Draft</DialogTitle>
          </div>
          <DialogDescription>
            Review and edit before sending. {task.client?.name ? `To: ${task.client.name}` : ''}
          </DialogDescription>
        </DialogHeader>

        {draft.state === 'loading' && (
          <div className="space-y-3 py-4">
            <SkeletonRow label="From" />
            <SkeletonRow label="To" />
            <SkeletonRow label="Subject" />
            <SkeletonRow label="Body" lines={4} />
            <SkeletonRow label="Questions" lines={2} />
          </div>
        )}

        {draft.state === 'error' && (
          <div className="space-y-3 py-2">
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{draft.errorMessage ?? 'Failed to draft update.'}</span>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={fetchDraft}>
              Try again
            </Button>
          </div>
        )}

        {draft.state === 'ready' && (
          <div className="space-y-4 py-2">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
              <Field label="From">
                <Select
                  value={draft.fromEmail}
                  onValueChange={(fromEmail) => setDraft((prev) => ({ ...prev, fromEmail }))}
                  disabled={effectiveFromOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        gmailAliasesQuery.isLoading && isGmailReplyMode
                          ? 'Loading aliases'
                          : 'Sender'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {effectiveFromOptions.map((email) => (
                      <SelectItem key={email} value={email}>
                        {email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!isGmailReplyMode && draft.senderSource === 'gmail' && (
                  <p className="text-[11px] text-muted-foreground">
                    Sending from connected Gmail{draft.connectedEmail ? `: ${draft.connectedEmail}` : ''}.
                  </p>
                )}
                {!isGmailReplyMode && draft.senderSource === 'fallback' && (
                  <p className="text-[11px] text-muted-foreground">
                    No Gmail sender is connected; Send Update will use the Make fallback.
                  </p>
                )}
                {isGmailReplyMode && effectiveFromOptions.length === 0 && (
                  <p className="text-[11px] text-destructive">
                    Connect Gmail in Settings before replying on this Gmail thread.
                  </p>
                )}
              </Field>

              <Field label="To">
                <input
                  type="text"
                  value={draft.toEmailsText}
                  onChange={(e) => setDraft((prev) => ({ ...prev, toEmailsText: e.target.value }))}
                  placeholder="client@example.com, teammate@example.com"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-[13px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </Field>
            </div>

            {linkedEmails.length > 0 && (
              <Field label="Delivery">
                <Select value={sendMode} onValueChange={setSendMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">
                      Send client update ({draft.senderSource === 'gmail' ? 'Gmail' : 'Make fallback'})
                    </SelectItem>
                    {linkedEmails.map((item) => (
                      <SelectItem key={item.recordId ?? item.id} value={item.recordId ?? item.id}>
                        Reply on linked Gmail thread: {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {draft.recipientOptions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {draft.recipientOptions.map((option) => (
                  <Button
                    key={option.email}
                    type="button"
                    size="sm"
                    variant={
                      splitEmails(draft.toEmailsText).includes(option.email)
                        ? 'secondary'
                        : 'outline'
                    }
                    className="h-7 max-w-full truncate px-2 text-[11px]"
                    onClick={() => addRecipient(option.email)}
                  >
                    {option.name} <span className="font-normal opacity-70">{option.email}</span>
                  </Button>
                ))}
              </div>
            )}

            <Field label="Subject">
              <input
                type="text"
                value={draft.subject}
                onChange={(e) => setDraft((prev) => ({ ...prev, subject: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-[13px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </Field>

            <Field label="Body (markdown)">
              <textarea
                value={draft.body}
                onChange={(e) => setDraft((prev) => ({ ...prev, body: e.target.value }))}
                rows={10}
                className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-[12px] leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </Field>

            <Field
              label={`Questions for client${draft.questions.length ? ` (${draft.questions.length})` : ''}`}
            >
              <div className="space-y-2">
                {draft.questions.length === 0 && (
                  <p className="text-[12px] italic text-muted-foreground">
                    No questions. Add one below if needed.
                  </p>
                )}
                {draft.questions.map((q, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 rounded-md border border-border bg-card px-2 py-1.5"
                  >
                    <textarea
                      value={q}
                      onChange={(e) => updateQuestion(idx, e.target.value)}
                      rows={1}
                      className="min-w-0 flex-1 resize-none bg-transparent text-[13px] focus:outline-none"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => removeQuestion(idx)}
                      aria-label="Remove question"
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addQuestion}
                  className="h-7 gap-1.5"
                >
                  <Plus className="h-3 w-3" aria-hidden />
                  Add question
                </Button>
              </div>
            </Field>

            {sendError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <span>{sendError}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={sendMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => sendMutation.mutate()}
            disabled={
              draft.state !== 'ready' ||
              sendMutation.isPending ||
              !draft.subject.trim() ||
              !draft.body.trim() ||
              splitEmails(draft.toEmailsText).length === 0 ||
              !effectiveFromOptions.includes(draft.fromEmail)
            }
            className="gap-1.5"
          >
            {sendMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Send className="h-3.5 w-3.5" aria-hidden />
            )}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function splitEmails(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[,\n;]/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

function SkeletonRow({ label, lines = 1 }: { label: string; lines?: number }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="space-y-1.5">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn('h-3 animate-pulse rounded-sm bg-muted', i === lines - 1 && 'w-3/4')}
          />
        ))}
      </div>
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === 'openai_not_configured') {
      return 'OpenAI is not configured on the Worker. Set OPENAI_API_KEY to enable AI drafts.';
    }
    if (err.code === 'webhook_not_configured') {
      return 'Send Update is not configured. Set MAKE_ORCHESTRATOR_WEBHOOK_URL and X_MAKE_APIKEY on the Worker.';
    }
    if (err.code === 'ai_unavailable') {
      return 'AI is temporarily unavailable. Try again in a moment.';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Send failed';
}
